import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert } from '../components';
import { FaUserPlus, FaUser, FaEnvelope, FaLock, FaCheckCircle } from 'react-icons/fa';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    // 验证姓名
    if (!name.trim()) {
      setError('请输入您的姓名');
      return false;
    }
    
    // 验证电子邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email)) {
      setError('请输入有效的电子邮箱地址');
      return false;
    }
    
    // 验证密码长度
    if (password.length < 6) {
      setError('密码长度至少为6位');
      return false;
    }
    
    // 验证密码匹配
    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 表单验证
    if (!validateForm()) {
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      // 调用注册接口
      await register(name, email, password);
      
      // 注册成功
      setSuccess(true);
      
      // 3秒后重定向到仪表盘
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || '注册失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4 p-md-5">
              <div className="text-center mb-4">
                <h2 className="fw-bold mb-1">创建账号</h2>
                <p className="text-muted">加入 WebChangeAI，开始监控您的网站</p>
              </div>
              
              {error && (
                <Alert 
                  type="danger"
                  message={error}
                  dismissible={true}
                  onClose={() => setError('')}
                />
              )}
              
              {success && (
                <Alert 
                  type="success"
                  message="注册成功！正在跳转到仪表盘..."
                  dismissible={false}
                  autoHideDuration={3000}
                />
              )}
              
              <form onSubmit={handleSubmit}>
                {/* 姓名输入框 */}
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    姓名
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaUser />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      placeholder="您的姓名"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={loading || success}
                      required
                    />
                  </div>
                </div>
                
                {/* 邮箱输入框 */}
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    邮箱地址
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaEnvelope />
                    </span>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading || success}
                      required
                    />
                  </div>
                </div>
                
                {/* 密码输入框 */}
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    密码
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaLock />
                    </span>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder="至少6位字符"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading || success}
                      required
                    />
                  </div>
                  <small className="form-text text-muted">密码长度至少为6位</small>
                </div>
                
                {/* 确认密码输入框 */}
                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label">
                    确认密码
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaCheckCircle />
                    </span>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      placeholder="再次输入密码"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading || success}
                      required
                    />
                  </div>
                </div>
                
                {/* 注册按钮 */}
                <div className="d-grid mb-4">
                  <button 
                    type="submit" 
                    className="btn btn-primary py-2"
                    disabled={loading || success}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        注册中...
                      </>
                    ) : (
                      <>
                        <FaUserPlus className="me-2" />
                        注册
                      </>
                    )}
                  </button>
                </div>
                
                {/* 登录链接 */}
                <div className="text-center">
                  <p className="mb-0">
                    已有账号? <Link to="/login" className="text-decoration-none">登录</Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;